/*
 * Project: bellcloud
 * File: apps/frontend/src/api/monitoring/useMonitoring.ts
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: Monitoring API Hooks (React-Query)
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import { POLL_MONITORING_LATEST_MS, POLL_PUSH_HISTORY_MS } from '@/api/query/polling';
import type {
  MonitoringLatestParams as UseMonitoringDataParams,
  MonitoringDataResponse,
  PlaybackMonitoringRequest,
  PlaybackMonitoringData,
  PushNotificationHistoryParams,
  PushNotificationHistoryItem,
} from '@packages/types/monitoring/monitoringData';

// Re-export for backward compatibility
export type {
  
  
  PlaybackMonitoringData,
  
  PushNotificationHistoryItem,
};

// ====================================
// Query Keys (캐시 관리)
// ====================================

export const monitoringKeys = {
  all: ['monitoring'] as const,
  latest: (companyId?: number | null, siteId?: number | null, isConnected?: boolean | null) =>
    [...monitoringKeys.all, 'latest', companyId, siteId, isConnected] as const,
  history: () => [...monitoringKeys.all, 'history'] as const,
  historyByParams: (params: PlaybackMonitoringRequest) =>
    [...monitoringKeys.history(), params] as const,
  availableDates: (serialNumber: string, deviceType: string) =>
    [...monitoringKeys.all, 'available-dates', serialNumber, deviceType] as const,
  pushHistory: (params: PushNotificationHistoryParams) =>
    [...monitoringKeys.all, 'push-history', params] as const,
};

// ====================================
// Queries
// ====================================

/**
 * 최신 모니터링 데이터 조회
 * GET /api/monitoring/latest
 */
export const useMonitoringData = ({
  companyId,
  siteId,
  isConnected,
  enabled = true,
}: UseMonitoringDataParams) => {
  return useQuery<MonitoringDataResponse[]>({
    queryKey: monitoringKeys.latest(companyId, siteId, isConnected),
    queryFn: async () => {
      const queryParams: Record<string, string | number | boolean> = {};
      if (companyId !== null) queryParams.company_id = companyId;
      if (siteId !== null) queryParams.site_id = siteId;
      if (isConnected !== null) queryParams.is_connected = isConnected;

      const response = await apiHelpers.get<MonitoringDataResponse[]>(
        'monitoring.latest', undefined, queryParams
      );
      return extractApiData(response);
    },
    enabled: enabled,
    refetchInterval: POLL_MONITORING_LATEST_MS,
  });
};

/**
 * 모니터링 데이터가 존재하는 날짜 목록 조회
 * GET /api/monitoring/available-dates?serial_number=xxx&deviceType=belllite
 */
export const useMonitoringAvailableDates = ({
  serialNumber,
  deviceType,
  enabled = true,
}: {
  serialNumber: string;
  deviceType: string;
  enabled?: boolean;
}) => {
  return useQuery<string[]>({
    queryKey: monitoringKeys.availableDates(serialNumber, deviceType),
    queryFn: async () => {
      const response = await apiHelpers.get<string[]>(
        'monitoring.availableDates', undefined, {
          serial_number: serialNumber,
          deviceType,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      );
      return extractApiData(response);
    },
    enabled: enabled && !!serialNumber && !!deviceType,
  });
};

// ====================================
// Mutations
// ====================================

/**
 * Playback 모니터링 데이터 조회 (검색 트리거 — 캐시 스냅샷이 아닌 단발 요청)
 * POST /api/monitoring/history
 */
export const useSearchPlaybackMonitoring = () => {
  return useMutation<PlaybackMonitoringData, Error, PlaybackMonitoringRequest>({
    mutationFn: async (params: PlaybackMonitoringRequest) => {
      const response = await apiHelpers.post<PlaybackMonitoringData>(
        'monitoring.history',
        { ...params, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
      );
      return extractApiData(response);
    },
  });
};

/**
 * Push Notification History 조회
 * GET /api/monitoring/push-history
 *
 * 주기 폴링 없음: 이력은 서버에 기록된 시점의 스냅샷이며, 새 푸시가 “도착”한다고 해서
 * 이 목록이 실시간 스트림처럼 갱신되는 모델이 아님. 갱신은 queryKey(필터/기간) 변경,
 * 마운트, 창 포커스 등 기본 useQuery 동작에 맡김. 실시간 디바이스 상태는 useMonitoringData 폴링.
 */
export const usePushNotificationHistory = ({
  companyId,
  siteId,
  serialNumber,
  startDate,
  endDate,
  notificationType,
  success,
  enabled = true,
}: PushNotificationHistoryParams) => {
  return useQuery<PushNotificationHistoryItem[]>({
    queryKey: monitoringKeys.pushHistory({
      companyId,
      siteId,
      serialNumber,
      startDate,
      endDate,
      notificationType,
      success,
    }),
    queryFn: async () => {
      const queryParams: Record<string, string | number | boolean> = {};
      if (companyId !== null && companyId !== undefined) queryParams.company_id = companyId;
      if (siteId !== null && siteId !== undefined) queryParams.site_id = siteId;
      if (serialNumber) queryParams.serial_number = serialNumber;
      if (startDate) queryParams.start_date = startDate;
      if (endDate) queryParams.end_date = endDate;
      if (notificationType) queryParams.notification_type = notificationType;
      if (success !== null && success !== undefined) queryParams.success = success;

      const response = await apiHelpers.get<PushNotificationHistoryItem[]>(
        'monitoring.pushHistory', undefined, queryParams
      );
      return extractApiData(response);
    },
    enabled: enabled,
    refetchInterval: POLL_PUSH_HISTORY_MS,
  });
};
